'use client';
import { useResource } from '@/lib/use-resource';

// Alpha Zone rebalance event log — paired entrant/exit bubbles per rotation.
// Replaces the prior 3-column "today's roster + recent entries + discarded"
// layout. Reads from the canonical /api/alpha-basket file (monthly cadence,
// chained-leg accounting). The roster itself is shown by the Holdings table
// next to this panel; this panel is purely the rotation log.

interface RebalanceRow {
  date: string;
  entrant: { symbol: string; name: string; sector: string };
  exit: { symbol: string; name: string; sector: string; returnPct: number };
  slotIdx: number;
}
interface InceptionRow {
  date: string;
  type: 'inception';
  symbols: string[];
}
type EventRow = RebalanceRow | InceptionRow;

interface ApiShape {
  basket: { inception: string; lastRebalanceDate: string; nextRebalanceDate: string };
  events: EventRow[];
}

function isInception(e: EventRow): e is InceptionRow {
  return (e as InceptionRow).type === 'inception';
}

function formatPct(x: number): string {
  const pct = x * 100;
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}

export function AlphaZoneChanges() {
  const { data, error } = useResource<ApiShape>('/api/alpha-basket');

  if (error) {
    return <div className="text-[11px] text-red-400">Failed to load basket events.</div>;
  }
  if (!data) {
    return <div className="text-[11px] text-[#64748b]">Loading basket events…</div>;
  }

  const { basket, events } = data;
  const rebalances = events.filter((e): e is RebalanceRow => !isInception(e));
  const inception = events.find(isInception);

  return (
    <section className="bg-[#0d1117] border border-[#1e293b] rounded-lg overflow-hidden">
      <div className="px-3 py-2 border-b border-[#1e293b] flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-[12px] uppercase tracking-wider text-amber-300 font-semibold">Alpha Zone rotations</div>
          <div className="text-[10px] text-[#64748b] mt-0.5">
            Monthly rebalance · Inception {basket.inception} · Last {basket.lastRebalanceDate} · Next {basket.nextRebalanceDate}
          </div>
        </div>
        <div className="text-[10px] text-[#64748b]">
          {rebalances.length} {rebalances.length === 1 ? 'rotation' : 'rotations'} since inception
        </div>
      </div>

      <div className="px-3 py-3 space-y-1.5">
        {rebalances.length === 0 && (
          <div className="text-[11px] text-[#64748b] italic px-1">
            No rotations yet — basket is locked from {basket.inception} until {basket.nextRebalanceDate}.
          </div>
        )}

        {rebalances.map((r, i) => (
          <div
            key={`${r.slotIdx}-${r.date}-${i}`}
            className="flex items-center gap-3 text-[11px] py-1"
          >
            <span className="font-mono text-[10px] text-[#64748b] tabular-nums w-[78px] shrink-0">
              {r.date}
            </span>

            {/* Entrant bubble (green solid) */}
            <span
              className="inline-flex items-baseline gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40"
              title={`${r.entrant.name} · entered ${r.date}`}
            >
              <span className="font-mono font-bold text-emerald-200">{r.entrant.symbol}</span>
              <span className="text-[10px] text-emerald-300/80 truncate max-w-[140px]">{r.entrant.name}</span>
            </span>

            <span className="text-[#64748b]">→</span>

            {/* Exit bubble (red outline, strike) */}
            <span
              className="inline-flex items-baseline gap-1.5 px-2 py-0.5 rounded-full border border-red-500/40 bg-red-500/5"
              title={`${r.exit.name} · exited ${r.date}`}
            >
              <span className="font-mono font-bold text-red-300 line-through decoration-red-500/60">{r.exit.symbol}</span>
              <span className="text-[10px] text-red-300/70 truncate max-w-[140px] line-through decoration-red-500/40">{r.exit.name}</span>
            </span>

            <span
              className={`font-mono tabular-nums text-[11px] ${r.exit.returnPct >= 0 ? 'text-emerald-300' : 'text-red-300'}`}
            >
              {formatPct(r.exit.returnPct)}
            </span>
          </div>
        ))}

        {inception && (
          <div className="flex items-center gap-3 text-[11px] py-1 pt-2 border-t border-[#1e293b]">
            <span className="font-mono text-[10px] text-[#64748b] tabular-nums w-[78px] shrink-0">
              {inception.date}
            </span>
            <span className="inline-flex items-baseline gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30">
              <span className="font-mono font-bold text-amber-200">★ Inception</span>
              <span className="text-[10px] text-amber-300/80">{inception.symbols.length} founding names</span>
            </span>
          </div>
        )}
      </div>
    </section>
  );
}
